import fullBowlImg from '@/assets/images/dogs/full-bowl.png'
import halfBowlImg from '@/assets/images/dogs/half-bowl.png'
import { findMealByVariantId } from '@/utils/product'
import { FeedingPlanType } from '@lyka-pet-food/contracts'
import type { MealProduct } from '@repo/api-types'
import pluralize from 'pluralize'
import { toDollars } from './toDollars'

export type SubscriptionCadenceSource = {
  cadenceInWeeks: number
  customCadenceInWeeks?: number | null
}

export type SubscriptionPricingSource = SubscriptionCadenceSource & {
  feedingPlanTotalPrice: number
}

/** Delivery cadence the customer is actually on (custom CC cadence takes precedence). */
export function getEffectiveCadenceInWeeks(source: SubscriptionCadenceSource): number {
  return source.customCadenceInWeeks ?? source.cadenceInWeeks
}

/**
 * Plan-change preview APIs return standard plan cadence and standard-box pricing in
 * `cadenceInWeeks` / `feedingPlanTotalPrice` (see orchestration calculateSubscriptionPrice:
 * customBoxInWeeks is null, so pricing uses generated-plan cadence, not custom cadence).
 * When rendering the customer's current plan, use live subscription cadence and price.
 */
export function getReviewPlanDisplayPricing(
  reviewPlan: { cadenceInWeeks: number; feedingPlanTotalPrice: number },
  options: { isCurrentPlan: boolean; subscription?: SubscriptionPricingSource | null },
): { cadenceInWeeks: number; feedingPlanTotalPrice: number } {
  if (options.isCurrentPlan && options.subscription) {
    return {
      cadenceInWeeks: getEffectiveCadenceInWeeks(options.subscription),
      feedingPlanTotalPrice: options.subscription.feedingPlanTotalPrice,
    }
  }

  return {
    cadenceInWeeks: reviewPlan.cadenceInWeeks,
    feedingPlanTotalPrice: reviewPlan.feedingPlanTotalPrice,
  }
}

/** @see getReviewPlanDisplayPricing */
export function getReviewPlanDisplayCadence(
  reviewPlan: { cadenceInWeeks: number },
  options: { isCurrentPlan: boolean; subscription?: SubscriptionCadenceSource | null },
): number {
  if (options.isCurrentPlan && options.subscription) {
    return getEffectiveCadenceInWeeks(options.subscription)
  }

  return reviewPlan.cadenceInWeeks
}

export function formatDailyServingSize(servingSize?: number | null) {
  return servingSize ? `${servingSize}g / day` : '—'
}

export function formatPouches(totalPouches?: number, pouchSize?: number) {
  if (!totalPouches || !pouchSize) return '—'

  return `${totalPouches} x ${pouchSize}g ${pluralize('pouch', totalPouches)}`
}

export function formatMeals(skus: { productVariantId: string }[] = [], meals: MealProduct[] = []) {
  return skus
    .map((sku) => findMealByVariantId(meals, sku.productVariantId)?.name ?? '')
    .filter(Boolean)
    .join(', ')
}

export function getDailyPrice(totalPrice: number, cadenceInWeeks: number) {
  return parseFloat((totalPrice / (cadenceInWeeks * 7)).toFixed(2))
}

export function formatFrequency(cadence?: number) {
  if (!cadence) return '—'

  return `${cadence} ${pluralize('week', cadence)}`
}

export function formatPrice(value?: number) {
  return toDollars(value) ?? '—'
}

export function formatDailyPrice(totalPrice?: number, cadence?: number) {
  if (!totalPrice || !cadence || cadence === 0) return '—'

  const daily = totalPrice / (cadence * 7)

  return `${toDollars(daily)} / day`
}

export function getPlanMeta(planType?: FeedingPlanType) {
  if (planType === FeedingPlanType.full_bowl) {
    return {
      image: fullBowlImg,
      title: 'Full bowl plan',
      imageAlt: 'Full bowl plan image',
    }
  }

  if (planType === FeedingPlanType.half_bowl) {
    return {
      image: halfBowlImg,
      title: 'Half bowl plan',
      imageAlt: 'Half bowl plan image',
    }
  }

  return {
    image: undefined,
    title: undefined,
    imageAlt: undefined,
  }
}
